/**
 * WHAT THE VILLAGE WOULD PAY BACK TODAY — the live card under "Retirement village" on the Living
 * Arrangements step (owner, 8 Oct 2026). The exit entitlement laid out in the order the contract
 * applies it, every line from the fields as they are typed, adding to the one figure the snapshot's
 * total, net worth and the estate all carry (villageExit.villageInterestToday). The arithmetic is
 * the shared function's; this only names its parts.
 */
import type { DraftFactFind } from './initialFactFind.ts';
import type { FactFind } from '../../../shared/src/calculation/factFindTypes.ts';
import { villageExitEntitlement, villageInterestHeld, villageInterestToday, villageRefundMonths, villageFeesAfterLeaving, todayIsoOf, VILLAGE_REFUND_MONTHS_BY_STATE } from '../../../shared/src/calculation/support/villageExit.ts';
import { villageFeesAfterLeavingLine } from '../features/agedCare/villageExitWords.ts';
import { formatCurrency } from '../utils/index.ts';

export interface VillageValueLine {
    label: string;
    /** Formatted, signed where it is a deduction or an addition. */
    value: string;
    /** The total row. */
    total?: boolean;
}

export interface VillageValueInsightSection {
    title: string;
    subtitle: string;
    paragraphs: string[];
    lines: VillageValueLine[];
    icon: 'village';
    /** Never set here; present so the panel's section union stays one shape. */
    goalsIntro?: string | null;
    goalBlocks?: { label: string; summary: string }[];
}

const years = (n: number): string => {
    const r = Math.round(n * 10) / 10;
    return `${r} ${r === 1 ? 'year' : 'years'}`;
};

export function buildVillageValueInsightSection(factFind: DraftFactFind): VillageValueInsightSection | null {
    const ff = factFind as unknown as FactFind;
    if (!villageInterestHeld(ff)) return null;
    const v = ff.retirementVillage || {};
    const e = villageExitEntitlement(ff, todayIsoOf(new Date()));
    if (!e) return null;
    const entry = e.entryContribution;
    const feePct = Number(v.dmfPctPerYear) || 0;
    const capYears = Number(v.dmfCapYears) || 0;
    const feeYears = capYears > 0 ? Math.min(e.yearsResidency, capYears) : e.yearsResidency;
    const hasResidency = (typeof v.dateEntered === 'string' && v.dateEntered.length >= 10) || (Number(v.yearsResidency) || 0) > 0;
    const sharePct = Number.isFinite(Number(v.capitalGainSharePct)) && v.capitalGainSharePct != null ? Number(v.capitalGainSharePct) : 50;
    const loss = Math.max(0, entry - e.resaleValue);

    const lines: VillageValueLine[] = [{ label: 'Entry contribution', value: formatCurrency(entry) }];
    if (e.capitalGain > 0.5) {
        lines.push({ label: `Plus their ${sharePct}% share of the ${formatCurrency(Math.round(e.capitalGain))} gain on a ${formatCurrency(e.resaleValue)} resale`, value: `+${formatCurrency(Math.round(e.residentShareOfGain))}` });
    } else if (loss > 0.5) {
        lines.push({ label: `Less the shortfall on a ${formatCurrency(e.resaleValue)} resale`, value: `−${formatCurrency(Math.round(loss))}` });
    }
    if (e.departureFee > 0.5) {
        const basis = v.dmfBasis === 'resale' ? 'the resale price' : 'the entry contribution';
        lines.push({ label: `Less the departure fee (${feePct}% a year for ${years(feeYears)}${capYears > 0 ? `, capped at ${capYears}` : ''}, on ${basis})`, value: `−${formatCurrency(Math.round(e.departureFee))}` });
    }
    if (e.reinstatement > 0.5) lines.push({ label: 'Less reinstatement', value: `−${formatCurrency(Math.round(e.reinstatement))}` });
    lines.push({ label: 'Comes back', value: formatCurrency(Math.round(villageInterestToday(ff))), total: true });

    const paragraphs: string[] = [];
    if (feePct <= 0) {
        paragraphs.push('No departure fee is recorded, so the full entry contribution is taken as coming back. Enter the contract’s terms for a closer figure.');
    } else if (!hasResidency) {
        paragraphs.push('The departure fee has nothing to accrue over yet: add the date moved in, or the years in the village, and it is taken off here.');
    }
    const state = String(v.state || '').toUpperCase();
    const months = villageRefundMonths(v);
    const contractMonths = Number.isFinite(Number(v.refundMonths)) && v.refundMonths != null;
    const resaleSooner = (Number(v.expectedResaleMonths) || 0) > 0 && Number(v.expectedResaleMonths) <= months;
    paragraphs.push(
        resaleSooner
            ? `Paid within ${months} months of leaving — the unit is expected to resell by then, and the village pays within days of a sale.`
            : contractMonths
                ? `Paid within ${months} months of leaving, as the contract says.`
                : VILLAGE_REFUND_MONTHS_BY_STATE[state] != null
                    ? `Paid within ${months} months of leaving, the ${state} limit, unless the contract says sooner.`
                    : `Paid within ${months} months of leaving is assumed; choose the state, or enter the contract’s months, to set it.`,
    );
    // The old unit's fees after leaving, on the state's rule, for the wait.
    const fees = villageFeesAfterLeaving(v, todayIsoOf(new Date()), e.refundDate);
    if (fees.total > 0.5) paragraphs.push(villageFeesAfterLeavingLine(fees));
    paragraphs.push('This is what sits in net worth and the estate today. In later years the resale price grows and the fee keeps accruing to its cap, so the figure moves with them.');

    return {
        title: 'What the village would pay back today',
        subtitle: 'From the terms entered — updates as you type',
        paragraphs,
        lines,
        icon: 'village',
    };
}
